import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { LoadWeightCells, TripIntegrationDetails, EventActor } from 'components/Erp/IntegrationTrip';
import React, { useState, useCallback } from 'react';
import styles from '../ReportCard.module.scss';
import { DateTime, Vehicle, Duration } from 'components/Erp/Display';
import { MapPin, Map, X, Truck, SearchX, ChevronRight, ChevronDown } from 'lucide-react';
import { EmptyState } from 'components/Erp/Presentation';
import moment from 'moment';
import MiniMap from '../MiniMap';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import DriverIdentity from './DriverIdentity';
import { GeoField, InfoItem, PhoneLink } from './LoadDetailFields';
import { isFarFromKaraKeche } from '../utils/geo';
import { formatDuration } from '../utils/formatDuration';
import { parseDateToMoment } from '../utils/parseDate';
import { getDriverDocuments } from 'service/DriverService';
import { canReadDriverDocuments } from '../utils/access';
import { isSingleLoadClick, shouldToggleLoadRow } from '../utils/rowInteraction';

/**
 * Таблица текущих перевозок (in-route).
 */
const InRouteTableView = ({
  inRouteLoads,
  inRouteCount,
  inRouteNext,
  inRoutePrev,
  inRoutePage,
  setInRoutePage,
  inRouteSearch,
  setInRouteSearch,
  nowTime,
  user,
  loading = false,
  pending = false,
  error = null,
}) => {
  useInterfaceLanguage();
  const [visibleMaps, setVisibleMaps] = useState({});
  const [showLegend, setShowLegend] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMaps, setDetailMaps] = useState({});
  const [driverDocs, setDriverDocs] = useState({});
  const [docPreview, setDocPreview] = useState(null);

  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDetailMap = (mapKey) => {
    setDetailMaps(prev => ({ ...prev, [mapKey]: !prev[mapKey] }));
  };

  const fetchDocumentsIfNeeded = useCallback(async (item) => {
    if (!canReadDriverDocuments(user)) return;
    const driverId = item.driver_id || item.driver;
    if (!driverId) return;
    const itemId = item.id;
    if (driverDocs[itemId]) return;
    setDriverDocs(prev => ({ ...prev, [itemId]: { loading: true, error: null, docs: [] } }));
    try {
      const res = await getDriverDocuments(driverId);
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: null, docs: res.data } }));
    } catch (e) {
      setDriverDocs(prev => ({ ...prev, [itemId]: { loading: false, error: tr("Ошибка загрузки"), docs: [] } }));
    }
  }, [user, driverDocs]);

  const handleRowClick = (item) => {
    const id = item.id;
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
      setDetailMaps({});
      fetchDocumentsIfNeeded(item);
    }
  };

  const renderDetailCard = (item) => {
    const baseName = item.top_base_name || item.chpp_name || item.private_point_name || '—';
    const canSeeDocs = canReadDriverDocuments(user);
    const docsState = driverDocs[item.id];

    let timeAExit = null;
    if (item.time_a_exit) {
      timeAExit = moment(item.time_a_exit, 'DD.MM.YYYY HH:mm', true);
      if (!timeAExit.isValid()) timeAExit = moment(item.time_a_exit);
    }
    const transitDuration = timeAExit && timeAExit.isValid() ? formatDuration(timeAExit, nowTime) : '—';

    return (
      <div className={styles.detailCard}>
        <div className={styles.detailCardTopBar}>
          <div className={styles.detailCardTitle}>
            <span></span>{tr(" Подробности поездки")}</div>
          <div className={styles.detailDurationBadges}>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeLoading}`}>
              <Duration value={item.duration_str_loading} label={tr("Погрузка")} />
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeDelivery}`}>
              <Duration value={transitDuration} label={tr("В пути")} />
            </div>
          </div>
        </div>

        <div className={styles.detailBody}><TripIntegrationDetails load={item} />
          {tr(item.notes && (
            <div className={styles.detailNotes}>
              <strong>{tr("Примечания:")}</strong> {tr(item.notes)}
            </div>
          ))}
          {/* Водитель */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" Водитель")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("ФИО")} value={item.employee_name || '—'} />
              <InfoItem label={tr("Телефон")} value={<PhoneLink phone={item.employee_phone} />} />
              <InfoItem label={tr("Автомобиль")} value={<Vehicle number={item.vehicle_number} model={item.vehicle_model} />} />
            </div>
            {tr(canSeeDocs && (() => {
              const DOC_LABELS = ["Фото ТС","Паспорт (л)","Паспорт (о)","Права (л)","Права (о)","Тех.пасп. (л)","Тех.пасп. (о)"];
              const byDesc = {};
              if (docsState?.docs) docsState.docs.forEach(d => { if (d.description) byDesc[d.description] = d; });
              const otherDocs = docsState?.docs?.filter(d => !DOC_LABELS.includes(d.description)) || [];
              return (
                <div className={styles.detailDocsWrap}>
                  <div className={styles.detailDocsTitle}>{tr(" Документы водителя")}</div>
                  {!docsState || docsState.loading ? (
                    <div className={styles.detailDocsLoading}>{tr("Загрузка документов…")}</div>
                  ) : docsState.error ? (
                    <div className={styles.detailDocsError}>{tr(docsState.error)}</div>
                  ) : (
                    <>
                      <div className={styles.detailDocsGrid}>
                        {DOC_LABELS.map(label => {
                          const doc = byDesc[label];
                          const fileUrl = doc ? (doc.file_url || doc.file) : null;
                          const isActive = docPreview?.url === fileUrl && fileUrl;
                          return (
                            <button key={label}
                              className={`${styles.detailDocBtn} ${doc ? styles.detailDocBtnHas : styles.detailDocBtnMissing} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => { if (!fileUrl) return; setDocPreview(isActive ? null : { url: fileUrl, label }); }}
                              title={doc ? tr("Открыть: {{value0}}", { value0: label }) : tr("{{value0}} — не загружено", { value0: label })}
                            >
                              <span className={styles.detailDocBtnIcon}>{doc ? '' : '—'}</span>
                              <span className={styles.detailDocBtnLabel}>{tr(label)}</span>
                            </button>
                          );
                        })}
                        {otherDocs.map(doc => {
                          const fileUrl = doc.file_url || doc.file;
                          const isActive = docPreview?.url === fileUrl;
                          return (
                            <button key={doc.id}
                              className={`${styles.detailDocBtn} ${styles.detailDocBtnHas} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.description || doc.file_name || tr("Файл") })}
                            >
                              <span className={styles.detailDocBtnIcon}></span>
                              <span className={styles.detailDocBtnLabel}>{tr(doc.description || doc.file_name || tr("Файл"))}</span>
                            </button>
                          );
                        })}
                        {tr(!docsState?.docs?.length && (
                          <div className={styles.detailDocsEmpty} style={{ gridColumn: '1 / -1' }}>{tr("Документы не загружены")}</div>
                        ))}
                      </div>
                      {tr(docPreview && (
                        <div className={styles.detailDocPreview}>
                          <div className={styles.detailDocPreviewHeader}>
                            <div className={styles.detailDocPreviewTitle}>
                              <span className={styles.detailDocPreviewIcon}></span>
                              <span>{tr(docPreview.label)}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>{tr("↗ Открыть")}</a>
                              <button type="button" aria-label={tr("Закрыть документ")} className={styles.detailDocPreviewClose} onClick={() => setDocPreview(null)}><X size={16} aria-hidden="true" /></button>
                            </div>
                          </div>
                          <div className={styles.detailDocPreviewBody}>
                            <img src={docPreview.url} alt={tr(docPreview.label)} className={styles.detailDocPreviewImg}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>{tr("⇩ Скачать файл")}</a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })())}
          </div>

          {/* База */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" База назначения")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("Название")} value={<span style={{ color: "var(--theme-info-text, #0d6efd)", fontWeight: 700 }}>{tr(baseName)}</span>} />
              <InfoItem label={tr("Телефон")} value={<PhoneLink phone={item.top_base_data?.phone} />} />
              {tr((item.top_base_name ? tr("Топливная база") : item.chpp_name ? tr("ТЭЦ") : item.private_point_name ? tr("Соц. объект / МСУ") : null) && (
                <InfoItem label={tr("Тип")} value={item.top_base_name ? tr("Топливная база") : item.chpp_name ? tr("ТЭЦ") : tr("Соц. объект / МСУ")} />
              ))}
            </div>
            {tr(Array.isArray(item.geo_base) && item.geo_base.length > 0 && (
              <div className={styles.detailSubSection}>
                <div className={styles.detailSubSectionTitle}>{tr(" Точки базы")}</div>
                {item.geo_base.map((g, i) => (
                  <div key={g.id ?? i} className={styles.detailBranchRow}>
                    <span className={styles.detailBranchName}>{tr(g.name)}</span>
                    {tr(g.address && <span className={styles.detailBranchAddr}>{tr(g.address)}</span>)}
                    {tr(g.geo && (
                      <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${g.geo}`, '_blank')}>{tr(" Открыть")}</button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Въезд */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" Въезд в Кара-Кече")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("Время въезда")} value={<DateTime value={item.time_a} />} />
              <InfoItem label={tr("СБ въезда")} value={<EventActor source={item.entered_source} label={item.entered_actor_label} fallback={item.guard_name} entry />} />
              <InfoItem label={tr("Телефон СБ")} value={<PhoneLink phone={item.guard_phone} />} />
            </div>
            {tr(item.geo_a && (
              <div className={styles.detailGeoSection}>
                <span className={styles.detailGeoLabel}>{tr("Геолокация въезда")}</span>
                <GeoField geo={item.geo_a} mapKey={`detail-${item.id}-geo_a`} maps={detailMaps} onToggle={toggleDetailMap} />
              </div>
            ))}
          </div>

          {/* Выезд */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>{tr(" Выезд с Кара-Кече")}</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label={tr("Время выезда")} value={<DateTime value={item.time_a_exit} />} />
              <InfoItem label={tr("СБ выезда")} value={<EventActor source={item.exit_source} label={item.exit_actor_label} fallback={item.exit_guard_name} />} />
              <InfoItem label={tr("Телефон СБ")} value={<PhoneLink phone={item.exit_guard_phone} />} />
            </div>
            {tr(item.geo_a_exit && (
              <div className={styles.detailGeoSection}>
                <span className={styles.detailGeoLabel}>{tr("Геолокация выезда")}</span>
                <GeoField geo={item.geo_a_exit} mapKey={`detail-${item.id}-geo_a_exit`} maps={detailMaps} onToggle={toggleDetailMap} />
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className={styles.table_info_wrapper}>
      {tr(showLegend && (
        <div className={styles.legendOverlay} onClick={() => setShowLegend(false)}>
          <div className={styles.legendModal} onClick={e => e.stopPropagation()}>
            <div className={styles.legendHeader}>
              <span>{tr(" Значение цветов строк")}</span>
              <button type="button" aria-label={tr("Закрыть подсказку")} className={styles.legendClose} onClick={() => setShowLegend(false)}><X size={18} aria-hidden="true" /></button>
            </div>
            <div className={styles.legendBody}>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-surface, #fff)", border: "1px solid var(--theme-border, #dee2e6)" }} />
                <div>
                  <strong>{tr("Обычный фон")}</strong>
                  <div>{tr("Время в пути менее 1 суток или указано примечание")}</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-warning-soft, #fff7e7)" }} />
                <div>
                  <strong>{tr("Жёлтый")}</strong>
                  <div>{tr("Время в пути более 1 суток (без примечания)")}</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-orange-soft, #fff3e8)" }} />
                <div>
                  <strong>{tr("Оранжевый")}</strong>
                  <div>{tr("Время в пути более 2 суток (без примечания)")}</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-danger-soft, #fff0f0)" }} />
                <div>
                  <strong>{tr("Красный")}</strong>
                  <div>{tr("Время в пути более 3 суток (без примечания)")}</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-surface, #fff)", borderLeft: "4px solid var(--theme-danger-border, #dc3545)" }} />
                <div>
                  <strong>{tr("Красная полоса слева")}</strong>
                  <div>{tr("Машина далеко от Кара-Кече (аномальная геолокация)")}</div>
                </div>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendSwatch} style={{ background: "var(--theme-surface, #fff)", borderLeft: "4px solid var(--theme-warning-border, #ffc107)" }} />
                <div>
                  <strong>{tr("Жёлтая полоса слева")}</strong>
                  <div>{tr("Время с момента въезда более 60 часов")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      <Pagination
        page={inRoutePage}
        setPage={setInRoutePage}
        count={inRouteCount}
        hasNext={!!inRouteNext}
        hasPrev={!!inRoutePrev}
        shownCount={inRouteLoads.length}
        loading={loading || pending}
        onLegend={() => setShowLegend(true)}
      />

      <div className={styles.completedSearchRow}>
        <SearchBar
          value={inRouteSearch}
          onChange={setInRouteSearch}
          onClear={() => setInRouteSearch('')}
          placeholder={tr("Поиск по водителю, телефону, Базе, статусу, номеру машины, модели машины...")}
        />
        <span className={styles.completedBadge} style={{ background: "var(--theme-info-soft, #e3f2fd)", color: "var(--theme-info-text, #1565c0)", borderColor: "var(--theme-info-border, #90caf9)" }}>{tr(" В пути")}</span>
      </div>

      <div className={styles.tableScrollWrap}>
        <table className={`${styles.dataTable} ${styles.inRouteTable}`} aria-busy={loading || pending}>
          <thead>
            <tr>
              <th>#</th>
              <th>{tr("Машина")}</th>
              <th>{tr("Водитель")}</th>
              <th>{tr("Очередь")}</th><th>{tr("Масса тары")}</th><th>{tr("Брутто · нетто")}</th>
              <th>{tr("База назначения")}</th>
              <th>{tr("Въезд (Кара-Кече)")}</th>
              <th>{tr("Выезд (Кара-Кече)")}</th>
              <th>{tr("Время в пути")}</th>
              <th>{tr("Примечания")}</th>
            </tr>
          </thead>
          <tbody>
            {tr(!loading && !error && !inRouteLoads?.length && <tr><td colSpan={11} className={styles.emptyTableState}><EmptyState compact icon={inRouteSearch?.trim() ? SearchX : Truck} title={inRouteSearch?.trim() ? tr("По вашему запросу перевозки не найдены.") : tr("Перевозок в пути пока нет.")} description={inRouteSearch?.trim() ? tr("Измените запрос, чтобы увидеть другие перевозки.") : tr("Здесь отображаются поездки после выезда из Кара-Кече.")} /></td></tr>)}
            {(Array.isArray(inRouteLoads) ? inRouteLoads : []).map((item, index) => {
              const start = parseDateToMoment(item.time_a_exit || item.time_a);
              const diff = start ? moment.duration(nowTime.diff(start)) : null;
              const hours = diff ? Math.floor(diff.asHours()) : 0;
              const isFar = isFarFromKaraKeche(item.geo_a);
              const sequentialNumber = (inRoutePage - 1) * 10 + index + 1;


              const hasNotes = item.notes && item.notes.trim();
              let rowBg = {};
              if (!hasNotes && start && start.isValid()) {
                const hoursInRoute = nowTime.diff(start, 'hours');
                if (hoursInRoute >= 72) {
                  rowBg = { background: "var(--theme-danger-soft, #fff0f0)" };
                } else if (hoursInRoute >= 48) {
                  rowBg = { background: "var(--theme-orange-soft, #fff3e8)", color: "var(--theme-text, #3a3541)" };
                } else if (hoursInRoute >= 24) {
                  rowBg = { background: "var(--theme-warning-soft, #fff7e7)" };
                }
              }
              const borderStyle = isFar
                ? { borderLeft: "4px solid var(--theme-danger-border, #dc3545)" }
                : hours > 60 ? { borderLeft: "4px solid var(--theme-warning-border, #ffc107)" } : {};
              const rowStyle = { ...rowBg, ...borderStyle };

              const mapKeyA = `${item.id}-geo_a`;
              const mapKeyAExit = `${item.id}-geo_a_exit`;

              return (
                <React.Fragment key={item.id}>
                  <tr
                    style={{ ...rowStyle, cursor: 'pointer', background: expandedRowId === item.id ? "var(--theme-info-soft, #eef4ff)" : rowStyle.background }}
                    onClick={event => { if (shouldToggleLoadRow(event)) handleRowClick(item); }}
                    title={tr("Нажмите, чтобы открыть или скрыть подробности")}
                  >
                    <td data-label={tr("№")}><button type="button" className={styles.rowDetailButton} aria-label={tr("Подробнее о перевозке {{value0}}", { value0: item.vehicle_number || item.id })} aria-expanded={expandedRowId === item.id} aria-controls={`load-details-route-${item.id}`} onClick={event => { event.stopPropagation(); if (isSingleLoadClick(event)) handleRowClick(item); }}>{expandedRowId === item.id ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}{tr(sequentialNumber)}</button></td>
                    <td data-label={tr("Машина")}>
                      <Vehicle number={item.vehicle_number} model={item.vehicle_model} />
                    </td>
                    <td data-label={tr("Водитель")}>
                      <DriverIdentity name={item.employee_name} phone={item.employee_phone} />
                    </td>
                    <LoadWeightCells load={item} />
                    <td data-label={tr("База")} style={{ color: "var(--theme-info-text, #0d6efd)", fontWeight: '600' }}>
                      {tr(item.top_base_name || item.chpp_name || item.private_point_name || '-')}
                    </td>
                    <td data-label={tr("Въезд КК")}>
                      <div><DateTime value={item.time_a} /></div>
                      {tr(item.geo_a && (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                          <button onClick={event => { event.stopPropagation(); window.open(`https://www.google.com/maps?q=${item.geo_a}`, '_blank'); }} className={styles.geoLinkBtn} aria-label={tr("Открыть геолокацию на карте")}><MapPin size={16} /></button>
                          <button onClick={event => { event.stopPropagation(); toggleMap(item.id, 'geo_a'); }} aria-label={tr("Показать или скрыть мини-карту")} className={styles.mapToggleBtn}>{visibleMaps[mapKeyA] ? <X size={16} /> : <Map size={16} />}</button>
                        </div>
                      ))}
                    </td>
                    <td data-label={tr("Выезд КК")}>
                      <div><DateTime value={item.time_a_exit} /></div>
                      {tr(item.geo_a_exit && (
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                          <button onClick={event => { event.stopPropagation(); window.open(`https://www.google.com/maps?q=${item.geo_a_exit}`, '_blank'); }} className={styles.geoLinkBtn} aria-label={tr("Открыть геолокацию на карте")}><MapPin size={16} /></button>
                          <button onClick={event => { event.stopPropagation(); toggleMap(item.id, 'geo_a_exit'); }} aria-label={tr("Показать или скрыть мини-карту")} className={styles.mapToggleBtn}>{visibleMaps[mapKeyAExit] ? <X size={16} /> : <Map size={16} />}</button>
                        </div>
                      ))}
                    </td>
                    <td data-label={tr("В пути")} style={{ fontWeight: '700', color: hours > 60 ? "var(--theme-danger-text, #dc3545)" : "var(--theme-success-text, #198754)" }}>
                      <Duration value={item.time_a_exit ? formatDuration(item.time_a_exit, nowTime) : null} label={tr("В пути")} />
                    </td>
                    <td data-label={tr("Примечания")} style={{ fontSize: '14px' }}>{tr(item.notes || '-')}</td>
                  </tr>
                  {tr(item.geo_a && visibleMaps[mapKeyA] && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                          <MiniMap geo={item.geo_a} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tr(item.geo_a_exit && visibleMaps[mapKeyAExit] && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                          <MiniMap geo={item.geo_a_exit} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tr(expandedRowId === item.id && (
                    <tr id={`load-details-route-${item.id}`} className={styles.detailCardRow}>
                      <td colSpan={11}>
                        {renderDetailCard(item)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={inRoutePage}
        setPage={setInRoutePage}
        count={inRouteCount}
        hasNext={!!inRouteNext}
        hasPrev={!!inRoutePrev}
        shownCount={inRouteLoads.length}
        loading={loading || pending}
        style={{ marginTop: '8px', marginBottom: 0 }}
      />
    </div>
  );
};

export default React.memo(InRouteTableView);
